import { AutomationStatus, TestStep } from '../../../models/test-case.model';
import {
  SaveFullTestModuleRequest,
  TestModuleAutomationFramework,
  TestModuleDataSheetColumnMapping,
  TestModuleParameter,
  TestModuleStatus,
  TestModuleStepDraft,
} from '../../../models/test-module.model';
import { Testdatenblatt } from '../../../models/testdatenblatt.model';
import { isLocalId } from '../../test-case/test-step-table/test-step-local.util';

export interface ModuleFormRaw {
  title: string;
  description: string;
  status: TestModuleStatus;
  authorUserId: string;
  tags: string[];
  automationStatus: AutomationStatus;
  automationFramework: TestModuleAutomationFramework | null;
}

function nullable(value: string): string | null {
  return value || null;
}

function toStepDraft(step: TestStep): TestModuleStepDraft {
  const local = isLocalId(step.id);
  return {
    id: local ? null : step.id,
    clientId: local ? step.id : null,
    // NEX-546: Nur NEUE (lokale) Zeilen tragen die Bibliotheks-Referenz. Persistierte Zeilen
    // identifiziert das BE ueber `id` (id + testStepId zusammen -> 400); null fuer neu getippte.
    testStepId: local ? (step.libraryStepId ?? null) : null,
    kind: step.kind,
    title: step.title ?? null,
    action: step.action ?? null,
    expectedResult: step.expectedResult ?? null,
    notes: step.notes ?? null,
    tcdl: step.tcdl ?? null,
    status: step.status ?? null,
    calledModuleId: step.calledModuleId ?? null,
    stepRole: step.stepRole ?? null,
    category: step.category ?? null,
    assigneeUserId: step.assigneeUserId ?? null,
    tags: step.tags ?? [],
    dataSheetId: step.dataSheetId ?? null,
    dataSheetFilters: step.dataSheetFilters ?? null,
    stepCondition: step.stepCondition ?? null,
  };
}

// NEX-704: Neue (lokale) Parameter tragen clientId, persistierte die echte id — nie beides
// zusammen (das BE lehnt id + clientId sonst nicht ab, verlinkt aber falsch). OUTPUT trägt
// weder Pflicht-Flag noch Default; das setzt der Server ohnehin auf false/null.
function toInputDraft(parameter: TestModuleParameter): TestModuleParameter {
  const local = !parameter.id || isLocalId(parameter.id);
  return {
    id: local ? null : parameter.id,
    clientId: local ? (parameter.clientId ?? parameter.id ?? null) : null,
    name: parameter.name,
    type: parameter.type,
    required: parameter.required,
    defaultValue: parameter.defaultValue ?? null,
  };
}

function toOutputDraft(parameter: TestModuleParameter): TestModuleParameter {
  const local = !parameter.id || isLocalId(parameter.id);
  return {
    id: local ? null : parameter.id,
    clientId: local ? (parameter.clientId ?? parameter.id ?? null) : null,
    name: parameter.name,
    type: parameter.type,
    required: false,
    defaultValue: null,
  };
}

// parameters/returns sind optional: Aufrufer ohne Schnittstellen-UI (z. B. der
// Modul-Edit-Drawer) lassen sie weg → inputs/outputs bleiben undefined und das BE
// fasst den Bereich nicht an (kein Diff). Eine leere Liste bedeutet dagegen bewusst
// „alle löschen" — deshalb hier NICHT auf [] defaulten.
export function toSaveFullModuleRequest(
  raw: ModuleFormRaw,
  steps: TestStep[],
  parameters?: TestModuleParameter[],
  returns?: TestModuleParameter[],
  columnMappings?: TestModuleDataSheetColumnMapping[],
): SaveFullTestModuleRequest {
  return {
    name: raw.title,
    description: nullable(raw.description),
    status: raw.status,
    authorUserId: nullable(raw.authorUserId.trim()),
    tags: raw.tags,
    steps: steps.map(toStepDraft),
    // NEX-644: Eingebundene Module sind jetzt MODULE_REF-Schritte (in steps). Die leere
    // (nicht null!) embeds-Liste räumt migrierte Alt-Embeds serverseitig ab (Cutover).
    embeds: [],
    inputs: parameters?.map(toInputDraft),
    outputs: returns?.map(toOutputDraft),
    columnMappings: columnMappings?.map((m) => ({
      testdatenblattId: m.testdatenblattId,
      sheetColumn: m.sheetColumn,
      parameterName: m.parameterName,
    })),
    automationStatus: raw.automationStatus,
    automationFramework: raw.automationFramework,
  };
}

// NEX-1787: Das Panel zeigt für eine Spalte mit Parameternamen die Zuordnung auf diesen
// Parameter an, auch ohne Eintrag. Beim Speichern wird daraus ein echter Eintrag, damit
// Anzeige und gespeicherter Stand übereinstimmen. Vorhandene Einträge (auch geleerte) gewinnen.
// Einträge auf gelöschte Spalten oder Parameter fallen weg, sonst lehnt das BE mit 400 ab.
// Einträge zu nicht übergebenen Blättern fallen nur weg, wenn die Blattliste geladen wurde;
// nach einem Ladefehler wäre sonst jede Zuordnung verloren.
export function withNameDefaultMappings(
  mappings: TestModuleDataSheetColumnMapping[],
  sheets: Testdatenblatt[],
  parameterNames: string[],
  sheetsLoaded: boolean,
): TestModuleDataSheetColumnMapping[] {
  const names = new Set(parameterNames);
  const columnsBySheet = new Map(sheets.map((sheet) => [sheet.id, sheet.spalten]));
  const current = mappings.filter((m) => {
    const columns = columnsBySheet.get(m.testdatenblattId);
    const parameterKnown = !m.parameterName || names.has(m.parameterName);
    const columnKnown = columns ? columns.includes(m.sheetColumn) : !sheetsLoaded;
    return parameterKnown && columnKnown;
  });
  const mapped = new Set(current.map((m) => `${m.testdatenblattId}:${m.sheetColumn}`));
  const defaults = sheets.flatMap((sheet) =>
    sheet.spalten
      .filter((col) => names.has(col) && !mapped.has(`${sheet.id}:${col}`))
      .map((col) => ({ testdatenblattId: sheet.id, sheetColumn: col, parameterName: col })),
  );
  return [...current, ...defaults];
}
